import React, {
	useCallback,
	useMemo,
	useState,
} from 'react';
import {
	Col,
	Empty,
	Row,
} from 'antd';
import { Moment } from 'moment';
import { useQuery } from 'react-query';
import {
	DashboardTypes,
	FixtureTypes,
} from '@shared/utils/constants';
import isItemPortCall from '@shared/utils/isItemPortCall';
import { nowMoment } from '@shared/utils/date';
import { getFutureItineraryPortCalls } from '@shared/utils/getCurrentPortCallIndex';
import { Values } from '@shared/utils/objectEnums';
import { VoyageResponse } from '@api/features/voyages/getVoyages';
import { ItineraryPortCallDto } from '@client/screens/estimates/details/helpers/types';
import {
	getVoyageItinerary,
	getVoyages,
} from '@client/lib/api';
import { useAuth } from '@client/lib/auth';
import Table from '@client/components/Table/Table';
import Card from '@client/components/Card/Card';
import showErrorNotification from '@client/utils/showErrorNotification';
import { getVoyageListColumns } from '@client/screens/fleet/VoyagesListScreen/voyageListTableHelpers';
import PositionListItineraryTable from './PositionListItineraryTable';
import PositionListExport from './PositionListExport';
import styles from './styles/PositionListWidget.module.css';

const OUT_VOYAGE_TYPES = [
	FixtureTypes.SPOT,
	FixtureTypes.TC_OUT,
	FixtureTypes.BB_OUT,
] as Values<typeof FixtureTypes>[];

const PositionListWidget = ({
	setLastUpdated,
}: {
	setLastUpdated: React.Dispatch<React.SetStateAction<Moment | undefined>>;
}) => {
	const { userInfo } = useAuth();
	const [expandedRowKey, setExpandedRowKey] = useState<number | null>(null);
	const [itineraryCache, setItineraryCache] = useState<Map<number, ItineraryPortCallDto[]>>(new Map());
	const [loadingItineraryIds, setLoadingItineraryIds] = useState<Set<number>>(() => new Set());

	const personalVesselCacheKey = userInfo.filterVessels ?
		userInfo.relevantVessels.map((v) => v.id).sort((a, b) => a - b).join(',') :
		'all';

	const {
		data: voyages,
		isFetching: isLoading,
	} = useQuery(
		[DashboardTypes.HOME, personalVesselCacheKey],
		() => getVoyages({
			includeCompleted: false,
			disablePagination: true,
			voyageTypeFilter: [...OUT_VOYAGE_TYPES],
		}),
		{
			onSuccess: (_d) => setLastUpdated(nowMoment()),
			refetchOnWindowFocus: false,
		},
	);

	const rows = voyages?.rows ?? [];

	const loadItinerary = useCallback(async (voyageId: number) => {
		setLoadingItineraryIds((prev) => new Set(prev).add(voyageId));

		try {
			const result = await getVoyageItinerary(voyageId);
			const portCalls = (result ?? []).filter(isItemPortCall) as ItineraryPortCallDto[];
			const future = getFutureItineraryPortCalls(portCalls, nowMoment());

			setItineraryCache((prev) => new Map(prev).set(voyageId, future));
		} catch (error) {
			showErrorNotification('Could not load voyage itinerary', error as Error);
		} finally {
			setLoadingItineraryIds((prev) => {
				const next = new Set(prev);
				next.delete(voyageId);

				return next;
			});
		}
	}, []);

	const handleExpandedRowsChange = useCallback((keys: readonly React.Key[]) => {
		const nextKey = keys.length > 0 ? (keys[keys.length - 1] as number) : null;
		setExpandedRowKey(nextKey);

		if (nextKey != null && !itineraryCache.has(nextKey)) {
			void loadItinerary(nextKey);
		}
	}, [itineraryCache, loadItinerary]);

	const toggleRowExpand = useCallback((row: VoyageResponse) => {
		handleExpandedRowsChange(expandedRowKey === row.id ? [] : [row.id]);
	}, [expandedRowKey, handleExpandedRowsChange]);

	const columns = useMemo(
		() => getVoyageListColumns({
			compact: true,
			tagMaxWidth: 90,
			includeStatus: false,
			linkIdentifier: true,
			localFixingCompanyFilters: userInfo.fixingCompanies.map((c) => c.name),
		}),
		[userInfo.fixingCompanies],
	);

	return (
		<Row gutter={[16, 16]}>
			<Col span={24}>
				<Card 
					size="small"
					title="Position list"
					extra={<PositionListExport />}
					slim
				>
					<Table<VoyageResponse>
						className={styles.table}
						size="small"
						tableLayout="fixed"
						pagination={false}
						loading={isLoading}
						rowKey="id"
						dataSource={rows}
						columns={columns}
						onRowClick={toggleRowExpand}
						scroll={{ x: 1280 }}
						expandable={{
							expandedRowKeys: expandedRowKey != null ? [expandedRowKey] : [],
							onExpandedRowsChange: handleExpandedRowsChange,
							expandRowByClick: false,
							expandedRowRender: (row) => {
								const cached = itineraryCache.get(row.id);
								const isLoadingRow = loadingItineraryIds.has(row.id);

								if (isLoadingRow && cached == null) {
									return (
										<div className={styles.expandedRow}>
											Loading itinerary...
										</div>
									);
								}

								if (cached != null && cached.length === 0) {
									return (
										<div className={styles.expandedRow}>
											<Empty description="There are no planned port calls for this contract" />
										</div>
									);
								}

								if (cached == null) {
									return null;
								}

								return (
									<div className={styles.expandedRow}>
										<PositionListItineraryTable portCalls={cached} />
									</div>
								);
							},
						}}
					/>
				</Card>
			</Col>
		</Row>
	);
};

export default PositionListWidget;
